// src/components/BoardPack/pages/consolidate_boardpack/pl_summary_page.jsx
import React, { useEffect, useRef, useState } from "react";
import { useQueue } from "@/context/QueueContext";
import { useNavigate } from "react-router-dom";
import ErrorState from "@/components/common/ErrorStateComponent";
import LoadingComponent from "@/components/common/LoadingComponent";
import { apiCall } from "@/api/apiCall";
import { processParams } from "../../utils/paramsHelper";
import { TableCurrentMonthYear } from "../../components/tables/TableCurrentMonthYear";

const PL_Summary_Page = ({
  year = "",
  month = "",
  contract = "",
  page = null,
  isRefresh = false,
}) => {
  const { enqueue } = useQueue();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tableData, setTableData] = useState({ columns: [], rows: [] });
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    if (!year || !month || !contract) return;
    if (hasFetchedRef.current) return;

    hasFetchedRef.current = true;
    fetchData();
  }, [year, month, contract]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const apiParams = processParams(page?.params || {}, { year, month, contract });

      const response = await enqueue(() =>
        apiCall("GET", "/board-pack/page-one", apiParams, navigate, false, false, isRefresh)
      );

      if (!response?.success) {
        throw new Error(response?.message || "API returned unsuccessful response");
      }

      const { meta, data: apiRows } = response;

      const columns = [
        { year: meta.year, period: meta.month, type: "Actual" },
        { year: meta.year, period: meta.month, type: "Budget" },
        { year: meta.year, period: meta.month, type: "Variance Act V Bud" },
        { year: meta.year, period: meta.month, type: "% Variance Act V Bud" },
        { year: meta.year, period: meta.month, type: "Actual" },
        { year: meta.previousYear, period: meta.month, type: "Actual" },
        {
          year: `${meta.year} v ${meta.previousYear}`,
          period: meta.month,
          type: "Variance",
        },
        {
          year: `${meta.year} v ${meta.previousYear}`,
          period: meta.month,
          type: "% Variance",
        },
        {
          year: meta.year,
          period: `${meta.month} YTD`,
          type: "Actual",
          isYTD: true,
        },
        {
          year: meta.previousYear,
          period: `${meta.month} YTD`,
          type: "Actual",
          isYTD: true,
        },
        {
          year: `${meta.year} v ${meta.previousYear}`,
          period: `${meta.month} YTD`,
          type: "Variance",
          isYTD: true,
        },
        {
          year: `${meta.year} v ${meta.previousYear}`,
          period: `${meta.month} YTD`,
          type: "% Variance",
          isYTD: true,
        },
        {
          year: meta.year,
          period: `${meta.month} YTD`,
          type: "Actual",
          isYTD: true,
        },
        {
          year: meta.year,
          period: `${meta.month} YTD`,
          type: "Budget",
          isYTD: true,
        },
        {
          year: meta.year,
          period: `${meta.month} YTD`,
          type: "Variance Act V Bud",
          isYTD: true,
        },
        {
          year: meta.year,
          period: `${meta.month} YTD`,
          type: "% Variance Act V Bud",
          isYTD: true,
        },
      ];

      // Transform API response to table rows format
      const transformedRows = apiRows.map((item) => {
        const isTotal =
          item.label === "Total Revenue" ||
          item.label === "EBITDA" ||
          item.label === "OPERATING PROFIT/(LOSS)";

        const isSectionHeader =
          item.label === "Revenue" ||
          item.label === "Total Rent" ||
          item.label === "Zakat, Exceptional & CG" ||
          item.label === "Total Depreciation & Amortization";

        return {
          label: item.label,
          values: [
            item.block1.Actual || "-",
            item.block1.Budget || "-",
            item.block1.Variance || "-",
            item.block1["Variance %"] || "-",

            item.block2.Actual || "-",
            item.block2.Budget || "-",
            item.block2.Variance || "-",
            item.block2["Variance %"] || "-",

            item.block3.Actual || "-",
            item.block3.Budget || "-",
            item.block3.Variance || "-",
            item.block3["Variance %"] || "-",

            item.block4.Actual || "-",
            item.block4.Budget || "-",
            item.block4.Variance || "-",
            item.block4["Variance %"] || "-",
          ],
          isTotal,
          isSectionHeader,
        };
      });

      setTableData({
        columns,
        rows: transformedRows,
      });
    } catch (err) {
      console.error("Profit & Loss fetch error:", err);
      setError(err.message || "Failed to load data. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingComponent />;
  }

  if (error) {
    return <ErrorState error={error} onRetry={fetchData} />;
  }
  return (
    <div className="flex flex-col">
      <TableCurrentMonthYear
        title={page?.title}
        subtitle={page?.subtitle}
        columns={tableData.columns}
        rows={tableData.rows}
      />
      {/* New Comments Section */}
      <div className="mt-1.5 mx-2 !border-2 ">
        <div className="border border-black p-2 bg-white">
          <div className="text-center font-semibold text-sm mb-1 border-b border-transparent">
            Comments
          </div>
          <textarea className="w-full h-12 p-2 resize-none outline-none text-sm focus:ring-0 border-0 bg-transparent" />
        </div>
      </div>
    </div>
  );
};

export default PL_Summary_Page;
